import { Controller } from "@hotwired/stimulus"
import { computePosition, offset, shift, flip } from "@floating-ui/dom"
import { reactor } from "../lib/reactor"

export default class extends Controller {
  static targets = ["content"]
  static values = { placement: { type: String, default: "bottom" } }

  connect() {
    reactor(this)
    this.visible = false

    if (!this.element.hasAttribute("tabindex")) {
      this.element.tabIndex = 0
    }

    this.showHandler = () => this.show()
    this.hideHandler = () => this.hide()
    this.keyHandler = (event) => {
      if (event.key === "Escape") this.hide()
    }

    this.element.addEventListener("mouseenter", this.showHandler)
    this.element.addEventListener("mouseleave", this.hideHandler)
    this.element.addEventListener("focus", this.showHandler)
    this.element.addEventListener("blur", this.hideHandler)
    this.element.addEventListener("keydown", this.keyHandler)
  }

  disconnect() {
    this.element.removeEventListener("mouseenter", this.showHandler)
    this.element.removeEventListener("mouseleave", this.hideHandler)
    this.element.removeEventListener("focus", this.showHandler)
    this.element.removeEventListener("blur", this.hideHandler)
    this.element.removeEventListener("keydown", this.keyHandler)
    this.tooltip?.remove()
    this.element.removeAttribute("aria-describedby")
  }

  morph() {
    if (!this.visible) return

    if (!this.tooltip) return this.show()

    this.tooltip.innerHTML = this.contentTarget.innerHTML
    if (!this.tooltip.isConnected) document.body.appendChild(this.tooltip)
    this.reposition()
  }

  show() {
    this.visible = true
    this.tooltip?.remove()
    this.tooltip = this.createTooltip()

    Object.assign(this.tooltip.style, {
      position: "absolute",
      maxWidth: "min(24rem, calc(100vw - 2rem))",
      visibility: "hidden",
      left: "0",
      top: "0"
    })
    document.body.appendChild(this.tooltip)
    this.element.setAttribute("aria-describedby", this.tooltip.id)

    this.reposition().then(() => {
      requestAnimationFrame(() => this.tooltip?.classList.remove("opacity-0", "translate-y-2"))
    })
  }

  reposition() {
    return computePosition(this.element, this.tooltip, {
      placement: this.placementValue,
      middleware: [offset(20), flip(), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      Object.assign(this.tooltip.style, { left: `${x}px`, top: `${y}px`, visibility: "visible" })
    })
  }

  hide() {
    this.visible = false
    this.element.removeAttribute("aria-describedby")
    const tooltip = this.tooltip
    this.tooltip = null
    if (!tooltip) return

    tooltip.classList.add("opacity-0", "translate-y-2")
    tooltip.addEventListener("transitionend", () => tooltip.remove(), { once: true })
  }

  createTooltip() {
    const tooltip = document.createElement("div")
    tooltip.id = `tooltip-${this.element.id || crypto.randomUUID()}`
    tooltip.setAttribute("role", "tooltip")
    tooltip.innerHTML = this.contentTarget.innerHTML
    Object.assign(tooltip.style, {
      position: "absolute",
      maxWidth: "min(24rem, calc(100vw - 2rem))"
    })
    tooltip.classList.add(
      "absolute",
      "p-5",
      "bg-white",
      "rounded-xl",
      "border",
      "border-gray-300",
      "shadow-xl",
      "text-sm",
      "leading-relaxed",
      "text-gray-700",
      "dark:shadow-gray-900",
      "dark:bg-gray-900",
      "dark:border-gray-750",
      "dark:text-gray-on-gray-900",
      "transition",
      "duration-150",
      "ease-out",
      "opacity-0",
      "translate-y-2"
    )

    return tooltip
  }
}
